import { yupResolver } from '@hookform/resolvers/yup';
import type { FormEventHandler } from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import type { InferType } from 'yup';

import { fieldToDisplayableName } from 'utils/formatters/fieldToDisplayableName';
import { hasValue } from 'utils/hasValue';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { FormError } from '@atoms/FormError/FormError';

import { CancelButton } from '../ConfirmCancelDialog';
import { SpecificField } from '../fields/SpecificField';
import { setFormErrors } from '../formHelpers';
import type { StepProps } from '../steps';

export const CommonStep = ({
  onSubmit,
  onCancel,
  fields,
  defaultValues,
  responseField,
  fieldToDisplayableNameSchema = 'FIELD_NAMES',
  dataTestIdStepNextButton,
  isPayout = true,
  isLastStep
}: StepProps) => {
  const validationObject = useMemo(() => {
    if (hasValue(fields)) {
      return Object.keys(fields).reduce<Record<string, yup.BaseSchema>>(
        (acc, fieldName: string) => {
          acc[fieldName] = yup
            .string()
            .label(
              fieldToDisplayableName(fieldName, fieldToDisplayableNameSchema)
            )
            .required();
          return acc;
        },
        {}
      );
    }
  }, [fieldToDisplayableNameSchema, fields]);

  const schema = yup.object(validationObject).required();
  interface Inputs extends InferType<typeof schema> {
    base: null;
  }

  const form = useForm<Inputs>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: { ...defaultValues }
  });

  if (!fields) return null;

  const onSubmitHandle: FormEventHandler<HTMLFormElement> = async (e) => {
    try {
      await form.handleSubmit(async (data) => {
        await onSubmit(data);
      })(e);
    } catch (error: any) {
      setFormErrors({
        errors: error.error[responseField],
        setError: form.setError,
        fields: Object.keys(schema.fields)
      });
    }
  };

  const testIdPrefix = isPayout ? 'create-manual-payout-' : 'create-receiver-';

  return (
    <div className="flex justify-center pb-8">
      <form autoComplete="off" className="w-[90%]" onSubmit={onSubmitHandle}>
        {Object.keys(fields).map((fieldName) => {
          return (
            <SpecificField
              key={fieldName}
              fieldName={fieldName}
              fieldDefinition={fields[fieldName]}
              form={form}
              fieldToDisplayableNameSchema={fieldToDisplayableNameSchema}
              data-testid={`${testIdPrefix}${fieldName}`}
              responseField={responseField}
            />
          );
        })}
        <div className="mt-10 flex gap-4">
          <CancelButton onAction={onCancel} />
          <PrimaryButton
            size="large"
            htmlType="submit"
            className="w-full"
            data-testid={dataTestIdStepNextButton}
          >
            {isLastStep ? 'Submit' : 'Next'}
          </PrimaryButton>
        </div>
        <FormError message={form.formState.errors.base?.message} />
      </form>
    </div>
  );
};
